const DEFAULT_TTL_MS = 60 * 60 * 1000 // 1 hour

interface CacheEntry<T> {
  data: T
  ts: number
}

/**
 * Thin localStorage cache with TTL and stale-while-revalidate helper.
 *
 * @example
 * const cache = useLocalCache<Record<string, Price>>('producerPrices:' + cubeName)
 * const stale = cache.read()
 * if (stale) setProducerPrices(stale)
 * const fresh = await fetchPrices()
 * cache.write(fresh)
 */
export function useLocalCache<T>(key: string, ttlMs: number = DEFAULT_TTL_MS) {
  function read(): T | null {
    try {
      const raw = localStorage.getItem(key)
      if (!raw) { return null }
      const entry: CacheEntry<T> = JSON.parse(raw)
      return entry.data
    } catch {
      localStorage.removeItem(key)
      return null
    }
  }

  function isFresh(): boolean {
    try {
      const raw = localStorage.getItem(key)
      if (!raw) { return false }
      const entry: CacheEntry<T> = JSON.parse(raw)
      return Date.now() - entry.ts < ttlMs
    } catch {
      return false
    }
  }

  function write(data: T): void {
    try {
      const entry: CacheEntry<T> = { data, ts: Date.now() }
      localStorage.setItem(key, JSON.stringify(entry))
    } catch {
      // localStorage may be unavailable (private mode, quota exceeded) — fail silently
    }
  }

  function clear(): void {
    localStorage.removeItem(key)
  }

  return { read, isFresh, write, clear }
}
